"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@wtc/ui";
import type { SearchResult, SearchFilters, SearchFacets, FacetValue } from "./types";
import type { UseAdvancedAlgoliaSearchReturn } from "./types";
import { getHighlightedParts } from "./highlight-utils";
import { persistSession, loadSession, clearSession } from "./session-persistence";
import { SearchResultIcon } from "./SearchResultIcon";
import type { Database } from "@/lib/supabase/database.types";

// ============================================================================
// GLOBAL / CRM + EMAIL SEARCH (own-infra)
// ----------------------------------------------------------------------------
// Replaces the retired Algolia `hub` + `emails_search` reader with a fan-out to
// our own hybrid (FTS + vector + trigram) search routes:
//   POST /api/search/{contacts,companies,deals,tasks}?mode=hybrid  (CRM, RLS)
//   POST /api/search/emails?mode=hybrid                            (search_my_emails)
// The SearchResult[] shape, handleResultClick/result.url, facets, and the
// returned hook interface are preserved so the header search modal, /search
// page, and mobile drawer UX are unchanged.
// ============================================================================

type ContactRow = Database["public"]["Functions"]["search_contacts"]["Returns"][number];
type CompanyRow = Database["public"]["Functions"]["search_companies"]["Returns"][number];
type DealRow = Database["public"]["Functions"]["search_deals"]["Returns"][number];
type TaskRow = Database["public"]["Functions"]["search_tasks"]["Returns"][number];
type EmailRow = Database["public"]["Functions"]["search_my_emails"]["Returns"][number];

const CRM_LIMIT = 20;
const EMAIL_LIMIT = 8;

const EMPTY_FACETS: SearchFacets = {
  types: [],
  status: [],
  priority: [],
  tags: [],
};

/** POST a query to one own-infra search route and return its row array. */
async function postSearch<T>(
  path: string,
  query: string,
  limit: number,
  token: string | null
): Promise<T[]> {
  const res = await fetch(`${path}?mode=hybrid`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ query, limit }),
  });
  if (!res.ok) {
    throw new Error(`Search failed (${res.status})`);
  }
  // The CRM routes (contacts/companies/deals/tasks) return the cache envelope
  // `{ success, data, count, total_count }`; the emails route returns a bare row
  // array. Accept both so a shape mismatch never silently blanks an entity.
  const payload: unknown = await res.json();
  if (Array.isArray(payload)) return payload as T[];
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return (payload as { data: T[] }).data;
  }
  return [];
}

function mapContact(row: ContactRow): SearchResult {
  return {
    objectID: row.id,
    title: row.name || row.company_name || "Contact",
    description: row.title || undefined,
    type: "contact",
    url: `/contacts/${row.id}`,
    iconType: "contact",
    subtitle: row.email || row.phone || row.company_name || "",
    tags: row.tags || [],
    status: row.status || undefined,
  };
}

function mapCompany(row: CompanyRow): SearchResult {
  return {
    objectID: row.id,
    title: row.name || "Company",
    description: row.description || undefined,
    type: "company",
    url: `/companies/${row.id}`,
    iconType: "company",
    subtitle: row.industry || row.domain || row.website || row.status || "",
    tags: row.tags || [],
    status: row.status || undefined,
  };
}

function mapDeal(row: DealRow): SearchResult {
  const revenue = typeof row.revenue === "number" ? `$${row.revenue.toLocaleString()}` : "";
  const stage = row.stage || "";
  return {
    objectID: row.id,
    title: row.title || "Deal",
    description: row.description || undefined,
    type: "deal",
    url: `/deals/${row.id}`,
    iconType: "deal",
    subtitle: [stage, revenue].filter(Boolean).join(" • ") || row.company_name || "",
    tags: row.tags || [],
    status: row.stage || undefined,
  };
}

function mapTask(row: TaskRow): SearchResult {
  return {
    objectID: row.id,
    title: row.title || "Task",
    description: row.description || undefined,
    type: "task",
    url: `/tasks/${row.id}`,
    iconType: "task",
    subtitle: `${row.status || "Unknown"}${row.due_date ? ` • Due ${row.due_date}` : ""}`,
    tags: row.tags || [],
    status: row.status || undefined,
    priority: row.priority || undefined,
  };
}

function mapEmail(row: EmailRow): SearchResult {
  const direction = row.direction
    ? row.direction.charAt(0).toUpperCase() + row.direction.slice(1)
    : "Email";
  const date = row.occurred_at ? new Date(row.occurred_at).toLocaleDateString() : "";
  return {
    objectID: row.id,
    title: row.subject || "Email",
    description:
      typeof row.snippet === "string" ? row.snippet.substring(0, 100) : undefined,
    type: "email",
    // The [id] on the email detail page is a communications UUID (item 2/3).
    url: `/crm/emails/${row.id}`,
    iconType: "email",
    subtitle: [direction, date].filter(Boolean).join(" • "),
    status: row.status || undefined,
    createdAt: row.occurred_at || undefined,
  };
}

/** Count occurrences into a FacetValue[] (skips empty values). */
function countFacet(values: Array<string | undefined | null>): FacetValue[] {
  const counts = new Map<string, number>();
  for (const raw of values) {
    const value = typeof raw === "string" ? raw.trim() : "";
    if (!value) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
}

/** Derive facet buckets from the full (pre-filter) result set. */
function deriveFacets(results: SearchResult[]): SearchFacets {
  return {
    types: countFacet(results.map((r) => r.type)),
    status: countFacet(results.map((r) => r.status)),
    priority: countFacet(results.map((r) => r.priority)),
    tags: countFacet(results.flatMap((r) => r.tags ?? [])),
  };
}

/**
 * Client-side facet filtering. Mirrors the old Algolia facetFilters semantics:
 * values within one facet are OR'd, facets are AND'd across each other.
 */
function applyFacetFilters(results: SearchResult[], filters: SearchFilters): SearchResult[] {
  return results.filter((r) => {
    if (filters.types && filters.types.length > 0 && !filters.types.includes(r.type)) {
      return false;
    }
    if (
      filters.status &&
      filters.status.length > 0 &&
      !(r.status && filters.status.includes(r.status))
    ) {
      return false;
    }
    if (
      filters.priority &&
      filters.priority.length > 0 &&
      !(r.priority && filters.priority.includes(r.priority))
    ) {
      return false;
    }
    if (filters.tags && filters.tags.length > 0) {
      const rTags = r.tags ?? [];
      if (!filters.tags.some((t) => rTags.includes(t))) {
        return false;
      }
    }
    return true;
  });
}

export function useAdvancedAlgoliaSearch(): UseAdvancedAlgoliaSearchReturn {
  const { getAuthToken } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPersistedResults, setHasPersistedResults] = useState(false);
  const [filters, setFiltersState] = useState<SearchFilters>({});
  const [facets, setFacets] = useState<SearchFacets>(EMPTY_FACETS);
  const router = useRouter();

  /**
   * Monotonic id of the most recently STARTED search. Every fan-out captures its
   * own id and commits to state only while it is still the newest one.
   *
   * Without this, ordering is decided by whichever of five parallel POSTs the
   * network finishes last, not by what the rep typed last: type "acm", then
   * "acme" — if the "acm" fan-out lands second, its results overwrite "acme"'s
   * and the rep is looking at answers to a query that is no longer in the box.
   * Callers debounce (the mobile drawer at 300ms), which narrows the window but
   * cannot close it, because two searches are genuinely in flight whenever one
   * request outlives the debounce interval. `useFTSSearchWithFullRows` already
   * pins the same invariant for the entity-table search path.
   *
   * Clearing counts as a newer intent, so `clearSearch` and the empty-query
   * branch bump it too — otherwise an in-flight search repopulates a box the rep
   * has just emptied.
   */
  const latestSearchIdRef = useRef(0);

  // Load persisted search session on mount
  useEffect(() => {
    const session = loadSession();
    if (session && session.results.length > 0) {
      setQuery(session.query);
      setResults(session.results);
      setFiltersState(session.filters);
      setHasPersistedResults(true);
    }
  }, []);

  /**
   * Run a search and update state. ALSO returns the matched results so a caller
   * that needs them directly — the command palette, which renders its own list
   * rather than this hook's `results` state — does not have to re-implement the
   * fan-out across the entity search routes.
   */
  const searchAlgolia = useCallback(
    async (searchQuery: string, searchFilters?: SearchFilters): Promise<SearchResult[]> => {
      setQuery(searchQuery);
      const activeFilters = searchFilters ?? filters;
      const searchId = ++latestSearchIdRef.current;

      if (!searchQuery.trim()) {
        setResults([]);
        setFacets(EMPTY_FACETS);
        setHasPersistedResults(false);
        setIsLoading(false);
        clearSession();
        return [];
      }

      setIsLoading(true);
      setHasPersistedResults(false);

      try {
        const token = getAuthToken();

        // Fan out to every own-infra search route in parallel. allSettled so one
        // failing entity (or the email RPC) never blanks the whole result set.
        const [contacts, companies, deals, tasks, emails] = await Promise.allSettled([
          postSearch<ContactRow>("/api/search/contacts", searchQuery, CRM_LIMIT, token),
          postSearch<CompanyRow>("/api/search/companies", searchQuery, CRM_LIMIT, token),
          postSearch<DealRow>("/api/search/deals", searchQuery, CRM_LIMIT, token),
          postSearch<TaskRow>("/api/search/tasks", searchQuery, CRM_LIMIT, token),
          postSearch<EmailRow>("/api/search/emails", searchQuery, EMAIL_LIMIT, token),
        ]);

        const allResults: SearchResult[] = [
          ...(contacts.status === "fulfilled" ? contacts.value.map(mapContact) : []),
          ...(companies.status === "fulfilled" ? companies.value.map(mapCompany) : []),
          ...(deals.status === "fulfilled" ? deals.value.map(mapDeal) : []),
          ...(tasks.status === "fulfilled" ? tasks.value.map(mapTask) : []),
          ...(emails.status === "fulfilled" ? emails.value.map(mapEmail) : []),
        ];

        const filtered = applyFacetFilters(allResults, activeFilters);

        // A superseded search still resolves to ITS OWN results for the caller
        // that awaited it (the command palette renders its own list), but must
        // not touch shared state or overwrite the persisted session.
        if (searchId !== latestSearchIdRef.current) {
          return filtered;
        }

        // Facets reflect all matches; results reflect the active facet filter.
        setFacets(deriveFacets(allResults));
        setResults(filtered);
        persistSession(searchQuery, filtered, activeFilters);
        return filtered;
      } catch {
        if (searchId !== latestSearchIdRef.current) {
          return [];
        }
        setResults([]);
        setFacets(EMPTY_FACETS);
        return [];
      } finally {
        // Only the newest search owns the spinner — otherwise a slow older
        // request clears it while the current one is still running.
        if (searchId === latestSearchIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [filters, getAuthToken]
  );

  const handleResultClick = useCallback(
    (result: SearchResult, onClose?: () => void) => {
      onClose?.();
      router.push(result.url);
    },
    [router]
  );

  const clearSearch = useCallback(() => {
    // Newer intent than any in-flight fan-out, so their results are discarded.
    latestSearchIdRef.current += 1;
    setQuery("");
    setIsLoading(false);
    setResults([]);
    setFacets(EMPTY_FACETS);
    setHasPersistedResults(false);
    clearSession();
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  const setFilters = useCallback(
    (newFilters: SearchFilters) => {
      setFiltersState(newFilters);
      if (query.trim()) {
        void searchAlgolia(query, newFilters);
      }
    },
    [query, searchAlgolia]
  );

  const restoreSession = useCallback(() => {
    const session = loadSession();
    if (session) {
      setQuery(session.query);
      setResults(session.results);
      setFiltersState(session.filters);
      setHasPersistedResults(true);
    }
  }, []);

  const getResultIcon = useCallback((type: string) => {
    return React.createElement(SearchResultIcon, { type });
  }, []);

  // No-op retained for interface parity with the header search modal, which
  // calls this on open; own-infra search needs no key priming.
  const refreshSecuredKeys = useCallback(async () => {}, []);

  return {
    query,
    results,
    isLoading,
    hasPersistedResults,
    filters,
    facets,
    searchAlgolia,
    handleResultClick,
    clearSearch,
    clearFilters,
    setFilters,
    restoreSession,
    getHighlightedParts,
    getResultIcon,
    securedKeyError: null,
    refreshSecuredKeys,
  };
}
